'use client';

import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import classNames from 'classnames';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/16/solid';
import { getBackgroundProp, resolveSetting } from '../../../utils/settingsHelper';

export const getNavbarStyle = (props, settings) => ({
	background: getBackgroundProp(props, settings),
	color: resolveSetting(props.color, settings),
});

// `center` was the original boolean prop; `align` (left/center/right) replaced it.
// Old saved pages only have `center`, so fall back to it when `align` is unset.
export const getNavbarAlign = ({ align, center }) => align || (center ? 'center' : 'left');

export const getNavbarAlignClasses = (props) => {
	const align = getNavbarAlign(props);
	return {
		'mx-auto': align === 'center',
		'ml-auto': align === 'right',
	};
};

export function Navbar({ logo, items, right, settings, showMobileMenu = true, center, align, sticky, transparent, ...props }) {
	const [scrolled, setScrolled] = useState(false);
	const [navHidden, setNavHidden] = useState(false);
	const lastScrollY = useRef(0);

	useEffect(() => {
		if (!sticky) return;
		const onScroll = () => {
			const current = window.scrollY;
			if (transparent) setScrolled(current > 0);
			if (window.innerWidth < 768) {
				if (current > lastScrollY.current && current > 60) setNavHidden(true);
				else if (current < lastScrollY.current) setNavHidden(false);
			} else {
				setNavHidden(false);
			}
			lastScrollY.current = current;
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, [transparent, sticky]);

	const scrollTransparent = transparent && sticky;

	const navStyle = {
		...(scrollTransparent
			? (scrolled ? getNavbarStyle(props, settings) : { color: resolveSetting(props.color, settings) })
			: transparent
				? { color: resolveSetting(props.color, settings) }
				: getNavbarStyle(props, settings)),
		transition: sticky ? 'background 0.3s, transform 0.3s' : undefined,
		transform: navHidden ? 'translateY(-100%)' : undefined,
	};

	return (
		<Disclosure
			as="nav"
			style={navStyle}
			className={classNames({
				'sticky top-0 z-10': sticky && !transparent,
				'fixed top-0 left-0 w-full z-[9]': scrollTransparent,
				'absolute top-0 left-0 w-full z-[9]': transparent && !sticky,
			})}
		>
			{({ open }) => (
				<>
					<div className="px-2 @md:px-6 @lg:px-8 py-4" style={transparent && open && !scrolled ? getNavbarStyle(props, settings) : undefined}>
						<div className="relative flex items-center justify-between">
							{showMobileMenu && (
								<div className="inset-y-0 left-0 flex items-center @md:hidden">
									{/* Mobile menu button */}
									<DisclosureButton
										style={{ color: resolveSetting(props.color, settings) }}
										className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
									>
										<span className="absolute -inset-0.5" />
										<span className="sr-only">Open main menu</span>
										{open ? (
											<XMarkIcon className="block h-6 w-6" />
										) : (
											<Bars3Icon className="block h-6 w-6" />
										)}
									</DisclosureButton>
								</div>
							)}
							<div className="flex flex-1 items-center justify-center @md:justify-start">
								<div className="flex flex-shrink-0 items-center">
									{logo}
								</div>
								<div className={classNames('hidden @md:block', getNavbarAlignClasses({ align, center }))}>
									{items}
								</div>
							</div>
							<div className="hidden absolute inset-y-0 right-0 @md:flex items-center pr-2 @md:static @md:inset-auto @md:ml-6 @md:pr-0">
								{right}
							</div>
						</div>
					</div>

					<DisclosurePanel
						className="@md:hidden"
						style={transparent ? getNavbarStyle(props, settings) : undefined}
					>
						<div className="space-y-1 px-2 pb-3 pt-2">
							{items}
							{right}
						</div>
					</DisclosurePanel>
				</>
			)}
		</Disclosure>
	);
}
